import { RefreshCw } from 'lucide-react';
import { useSeasonSwitcher } from '../hooks/useSeasonSwitcher';

/**
 * A league's season-chip list for the season menu and switcher sheet: one chip
 * per known season, plus a "look for earlier seasons" row.
 *
 * @param {string} leagueId
 * @param {Function} authFetch
 * @param {number|null} activeSeason - the season currently in the URL, or null (live)
 * @param {(entry: {season: number, status: string}) => void} onSelectSeason
 * @param {string} [apiBaseUrl] - a share link's base, passed to the season requests
 * @param {boolean} [readOnly=false] - lists loaded seasons only, with no load or discover actions
 */
export function SeasonList({ leagueId, authFetch, activeSeason, onSelectSeason, apiBaseUrl, readOnly = false }) {
  const { seasons, loadingSeasons, failedSeasons, fetchError, discoverState, refetch, startBackfill, discoverEarlier } =
    useSeasonSwitcher(leagueId, authFetch, apiBaseUrl);

  if (seasons === null) {
    if (fetchError) {
      return (
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
            padding: '8px 10px', borderRadius: '6px', border: '1px dashed var(--ledger-bad)',
            background: 'transparent', fontSize: '12px', color: 'var(--ledger-bad)', cursor: 'pointer',
          }}
        >
          Failed to load seasons — try again
        </button>
      );
    }
    return <div style={{ fontSize: '12px', color: 'var(--ledger-muted)', padding: '6px 0' }}>Loading seasons...</div>;
  }

  const sorted = [...seasons].sort((a, b) => b.season - a.season);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {sorted.map(entry => {
        // A loaded season whose past weeks are still being built waits, like one still loading.
        const isLoading = loadingSeasons.has(entry.season) || (entry.status !== 'discoverable' && entry.ready === false);
        const isFailed = failedSeasons.has(entry.season);
        // activeSeason is null when viewing the live season (no ?season= in
        // the URL — see Task 1), so the live entry counts as active in that
        // case too, not only on an exact season-number match.
        const isChipActive = activeSeason === entry.season || (activeSeason === null && entry.status === 'live');

        if (entry.status === 'discoverable') {
          return readOnly ? null : (
            <button
              key={entry.season}
              onClick={() => startBackfill(entry.season)}
              disabled={isLoading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 10px', borderRadius: '6px', width: '100%', cursor: isLoading ? 'default' : 'pointer',
                background: 'var(--ledger-hover)', border: '1px solid var(--ledger-line)',
              }}
            >
              <span style={{ fontSize: '13px', color: 'var(--ledger-muted)' }}>{entry.season}</span>
              {isLoading ? (
                <RefreshCw className="animate-spin" style={{ width: '12px', height: '12px', color: 'var(--ledger-ink)' }} />
              ) : isFailed ? (
                <span style={{ fontSize: '11px', color: 'var(--ledger-bad)' }}>Failed — retry</span>
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--ledger-ink)' }}>Load this season</span>
              )}
            </button>
          );
        }

        return (
          <button
            key={entry.season}
            onClick={() => onSelectSeason(entry)}
            disabled={isLoading}
            style={{
              display: 'flex', alignItems: 'center', padding: '8px 10px', borderRadius: '6px', width: '100%', cursor: isLoading ? 'default' : 'pointer',
              background: isChipActive ? 'var(--ledger-current)' : 'var(--ledger-hover)',
              border: `1px solid ${isChipActive ? 'var(--ledger-line-2)' : 'var(--ledger-line)'}`,
            }}
          >
            <span style={{ fontSize: '13px', fontWeight: isChipActive ? 600 : 400, color: isChipActive ? 'var(--ledger-ink)' : 'var(--ledger-muted)' }}>{entry.season}</span>
          </button>
        );
      })}

      {!readOnly && discoverState !== 'exhausted' && (
        <button
          onClick={discoverEarlier}
          disabled={discoverState === 'loading'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '8px 10px', borderRadius: '6px', width: '100%', cursor: discoverState === 'loading' ? 'default' : 'pointer',
            background: 'transparent', border: '1px dashed var(--ledger-line-2)',
            fontSize: '12px', color: 'var(--ledger-muted)',
          }}
        >
          {discoverState === 'loading' ? 'Checking...' : discoverState === 'error' ? 'Failed — try again' : 'Look for earlier seasons'}
        </button>
      )}
    </div>
  );
}

export default SeasonList;
